import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useFontenehus } from "../context/FontenehusContext";
import { useUser } from "../context/UserContext";
import { useFontenehusActions } from "../hooks/useFontenehusActions";

const Header: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [menuOpen, setMenuOpen] = useState(false);
  const { user } = useUser();
  const [fontenehusModalOpen, setFontenehusModalOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const { selectFontenehus } = useFontenehusActions();

  // ✅ Close menu on outside click
  useEffect(() => {
    if (!menuOpen) return;

    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [menuOpen]);

  // ✅ Use context instead of local state
  const { fontenehusList, loading, selectedFontenehus, forceRefresh } =
    useFontenehus();

  // --- Logout handler ---
  const handleLogout = () => {
    localStorage.removeItem("jwtToken");
    localStorage.removeItem("user");
    localStorage.removeItem("selectedFontenehus");
    navigate("/");
  };

  // --- Back button handler ---
  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/main");
  };

  return (
    <>
      <header
        className="
          fixed top-0 left-0 right-0 z-[1001]
          flex flex-col sm:flex-row sm:items-center sm:justify-between
          bg-orangeBrand text-white shadow-md
          px-4 sm:px-6 py-2 sm:py-3
        "
      >
        {/* === Left: Logo + Back === */}
        <div className="flex items-center justify-between sm:justify-start w-full sm:w-auto">
          <div className="flex items-center gap-2">
            <h1
              className="text-lg sm:text-xl font-bold cursor-pointer select-none hover:opacity-90 transition truncate"
              onClick={() => navigate("/main")}
            >
              🍲 FonteneLunsj
            </h1>

            {location.pathname !== "/main" && (
              <button
                onClick={handleBack}
                className="text-2xl sm:text-lg ml-1 hover:opacity-80 transition"
                title="Tilbake"
              >
                ←
              </button>
            )}
          </div>

          {/* Mobile menu toggle */}
          <div className="flex items-center gap-2 sm:hidden">
            <button
              className="hover:opacity-80 transition text-2xl"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Meny"
            >
              {menuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* === Center Navigation (Desktop) === */}
        <nav
          className="
            hidden sm:flex items-center justify-center gap-6
            text-sm font-medium flex-1
          "
        >
          <button
            onClick={() => navigate("/main")}
            className="hover:opacity-80 transition"
          >
            Kalender
          </button>
          <button
            onClick={() => navigate("/oppskrifter")}
            className="hover:opacity-80 transition"
          >
            Oppskrifter
          </button>
          <button
            onClick={() => {
              const today = new Date().toISOString();
              navigate("/dagsmeny", {
                state: {
                  selectedDate: today,
                },
              });
            }}
            className="hover:opacity-80 transition"
          >
            Dagens
          </button>
          <button
            onClick={() => navigate(`/admin/`)}
            className="hover:opacity-80 transition"
            hidden={
              !user?.roles.includes("Admin") &&
              !user?.roles.includes("SuperAdmin")
            }
          >
            Admin
          </button>
          <button
            onClick={() => navigate("/om")}
            className="hover:opacity-80 transition"
          >
            Om
          </button>
        </nav>

        {/* === Right: Username + Fontenehus + Menu === */}
        <div className="hidden sm:flex items-center gap-3 sm:gap-4">
          <button
            onClick={() => setFontenehusModalOpen(true)}
            className="flex items-center gap-2 bg-white/10 text-white font-medium rounded-md px-3 py-1 border border-white/30 focus:outline-none hover:bg-white/20 transition text-sm"
          >
            🏠{" "}
            {selectedFontenehus
              ? selectedFontenehus.name
              : loading
                ? "Laster..."
                : "Velg hus"}
          </button>

          {user?.username && (
            <button
              onClick={() => navigate("/bruker/innstillinger")}
              className="hover:opacity-80 transition"
            >
              <span role="img" aria-label="user">
                👤
              </span>
              {user.displayName || user.username}
            </button>
          )}

          <button
            className="hover:opacity-80 transition text-2xl"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Meny"
          >
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {/* === Username + Fontenehus (Mobile) === */}
        <div className="sm:hidden flex flex-col items-center mt-1 text-sm font-medium opacity-90">
          {user?.username && (
            <button
              onClick={() => navigate("/bruker/innstillinger")}
              className="hover:opacity-80 transition"
            >
              <span role="img" aria-label="user">
                👤
              </span>
              {user.displayName || user.username}
            </button>
          )}
          <button
            onClick={() => setFontenehusModalOpen(true)}
            className="mt-1 flex items-center gap-1 text-white/90 hover:text-white transition"
          >
            🏠{" "}
            {selectedFontenehus
              ? selectedFontenehus.name
              : loading
                ? "Laster..."
                : "Velg hus"}
          </button>
        </div>

        {/* === Dropdown Menu === */}
        {menuOpen && (
          <div
            ref={dropdownRef}
            className="
              absolute top-full right-0 mt-1 w-48
              bg-white text-textDark shadow-lg rounded-lg
              overflow-hidden
            "
          >
            <button
              onClick={() => {
                navigate("/main");
                setMenuOpen(false);
              }}
              className="block w-full text-left px-4 py-2 hover:bg-orangeBrand/10"
            >
              Kalender
            </button>

            <button
              onClick={() => {
                navigate("/oppskrifter");
                setMenuOpen(false);
              }}
              className="block w-full text-left px-4 py-2 hover:bg-orangeBrand/10"
            >
              Oppskrifter
            </button>
            <button
              onClick={() => {
                navigate("/dagsmeny");
                forceRefresh(); // still optional, but useful if data changes
                setMenuOpen(false);
              }}
              className="block w-full text-left px-4 py-2 hover:bg-orangeBrand/10"
            >
              Dagens
            </button>

            <button
              onClick={() => {
                navigate("/admin/");
                forceRefresh(); // still optional, but useful if data changes
                setMenuOpen(false);
              }}
              className={
                "w-full text-left px-4 py-2 hover:bg-orangeBrand/10 " +
                (user?.roles?.includes("Admin") ||
                user?.roles?.includes("SuperAdmin")
                  ? "block"
                  : "hidden")
              }
            >
              Admin
            </button>

            <button
              onClick={() => {
                navigate("/om");
                setMenuOpen(false);
              }}
              className="block w-full text-left px-4 py-2 hover:bg-orangeBrand/10"
            >
              Om
            </button>

            <button
              onClick={() => {
                setFontenehusModalOpen(true);
                setMenuOpen(false);
              }}
              className="block w-full text-left px-4 py-2 hover:bg-orangeBrand/10"
            >
              🏠 Endre Fontenehus
            </button>
            {user?.username && (
              <button
                onClick={() => {
                  setMenuOpen(false);
                  navigate("/bruker/innstillinger");
                }}
                className="block w-full text-left px-4 py-2 hover:bg-orangeBrand/10"
              >
                <span role="img" aria-label="user">
                  👤
                </span>
                {user.displayName || user.username}
              </button>
            )}
            <button
              onClick={() => {
                handleLogout();
                setMenuOpen(false);
              }}
              className="block w-full text-left px-4 py-2 text-red-600 font-semibold hover:bg-orangeBrand/10"
            >
              Logg ut
            </button>
          </div>
        )}
      </header>

      {/* === Fontenehus Modal === */}
      {fontenehusModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm animate-fadeIn"
          onClick={() => setFontenehusModalOpen(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-lg p-6 w-full max-w-md mx-4 text-center relative animate-zoomIn"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-bold text-orangeBrand mb-4">
              Velg ditt Fontenehus
            </h2>

            {loading ? (
              <p className="text-gray-600">Laster...</p>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                {fontenehusList.map((hus) => (
                  <button
                    key={hus.id}
                    onClick={() => {
                      selectFontenehus(hus.id);
                      setFontenehusModalOpen(false);
                    }}
                    className={`flex flex-col items-center justify-center rounded-xl border-2 p-4 transition hover:shadow-md ${
                      selectedFontenehus?.id === hus.id
                        ? "border-orangeBrand bg-orangeBrand/10"
                        : "border-gray-200 hover:border-orangeBrand"
                    }`}
                  >
                    <span className="text-lg font-semibold text-textDark mb-1">
                      🏠
                    </span>
                    <span className="font-medium text-textDark">
                      {hus.name}
                    </span>
                  </button>
                ))}
              </div>
            )}

            <button
              onClick={() => setFontenehusModalOpen(false)}
              className="mt-6 text-sm text-gray-600 hover:text-orangeBrand font-medium"
            >
              Lukk
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
